<script lang="ts">
  /**
   * A one-time suggestion to install the game on this device, with an
   * Install button and a way to say no. The game decides when it shows
   * (installable, not installed, not dismissed before on this device) and
   * remembers the dismissal; the card only reports the two presses.
   */
  import type { Snippet } from 'svelte';

  import { shellText } from '../i18n.svelte.js';
  import PillButton from './PillButton.svelte';

  interface Props {
    readonly title: string;
    readonly detail?: string | undefined;
    readonly actionLabel: string;
    readonly onaction: () => void;
    readonly ondismiss: () => void;
    /** The dismiss button's name; the shell's own word by default. */
    readonly dismissLabel?: string | undefined;
    readonly icon?: Snippet | undefined;
    readonly testid?: string | undefined;
  }
  const { title, detail, actionLabel, onaction, ondismiss, dismissLabel, icon, testid }: Props =
    $props();
</script>

<aside class="ui-install" data-testid={testid}>
  {#if icon !== undefined}
    <span class="ui-install-icon" aria-hidden="true">{@render icon()}</span>
  {/if}
  <span class="ui-install-text">
    <b>{title}</b>
    {#if detail !== undefined}
      <span>{detail}</span>
    {/if}
  </span>
  <span class="ui-install-actions">
    <PillButton label={actionLabel} variant="light" testid="ui-install-action" onclick={onaction} />
    <button
      type="button"
      class="ui-install-dismiss"
      aria-label={dismissLabel ?? shellText('dismiss')}
      data-testid="ui-install-dismiss"
      onclick={ondismiss}
    >
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
        <path
          d="M6 6l12 12M18 6 6 18"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
        />
      </svg>
    </button>
  </span>
</aside>

<style>
  .ui-install {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--ui-space-3);
    padding: var(--ui-space-3) var(--ui-space-2) var(--ui-space-3) var(--ui-space-4);
    border-radius: var(--ui-radius-md);
    border: 1px dashed var(--ui-ink-2);
    background: var(--ui-ground);
    color: var(--ui-ink);
    font-family: var(--ui-font);
  }

  .ui-install-icon {
    flex: 0 0 auto;
    display: inline-flex;
    color: var(--ui-accent);
  }

  .ui-install-text {
    flex: 1 1 8rem;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
  }

  .ui-install-text b {
    font: var(--ui-text-body);
    font-weight: 800;
  }

  .ui-install-text span {
    font: var(--ui-text-caption);
    color: var(--ui-ink-2);
  }

  /* The action and its dismiss stay together: on a narrow card they move to a
     line of their own as one, at the right, rather than leaving the dismiss
     alone at the left. */
  .ui-install-actions {
    flex: 0 0 auto;
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    gap: var(--ui-space-1);
  }

  .ui-install-dismiss {
    flex: 0 0 auto;
    width: 2.75rem;
    height: 2.75rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 999px;
    background: transparent;
    color: var(--ui-ink-2);
  }
</style>
